import { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import DownloadForOfflineRoundedIcon from "@mui/icons-material/DownloadForOfflineRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  useOnlineQueryGate,
  ONLINE_QUERIES_OFF_REASON,
} from "../../General/OnlineQueryGate";
import { downloadFilePost } from "../../../services/ReportService";
import { refreshPaymentOrdersByLegalFileId } from "../../../services/PaymentOrderService";
import { formatDateTime } from "../../../config/Helper";
import DatagridCustom from "../../General/DatagridCustom";
import ReceiptPaymentDialog from "../../PaymentOrder/ReceiptPaymentDialog";

import GlassCard from "./GlassCard";
import SectionTitle from "./SectionTitle";
import { isLegalFileLocked, LOCKED_LEGAL_FILE_MESSAGE } from "./detailHelpers";
import LawyerSessionRequiredDialog from "../../General/Dialog/LawyerSessionRequiredDialog";

export default function TabSaldosOrdenes({ paymentOrders, judicialHistory, legalFileId, legalFile, onRefresh }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { online } = useOnlineQueryGate();
  const locked = isLegalFileLocked(legalFile);

  const [openPayment, setOpenPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refreshingPayments, setRefreshingPayments] = useState(false);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  const handleOpenPayment = (rowData) => {
    setSelectedPayment(rowData);
    setOpenPayment(true);
  };

  const handleClosePayment = () => {
    setOpenPayment(false);
    setSelectedPayment(null);
  };

  const handleRefreshPaymentOrders = async () => {
    if (!legalFileId) return;

    setRefreshingPayments(true);
    try {
      const res = await refreshPaymentOrdersByLegalFileId(legalFileId);

      if (res.data?.status === "success") {
        const newCount = res.data?.data?.new_payment_orders ?? 0;

        showToast(
          newCount > 0
            ? `Órdenes actualizadas. Nuevas órdenes: ${newCount}`
            : "Órdenes actualizadas. Sin cambios.",
          "success"
        );

        await onRefresh();
      } else {
        showToast(
          res.data?.message || "No se pudieron actualizar las órdenes.",
          "error"
        );
      }
    } catch (err) {
      if (err.response?.data?.code === "judicial_session_required") {
        setSessionRequiredLawyer(err.response.data.lawyer || {});
      } else {
        const msg =
          err.response?.data?.message || "Error al actualizar órdenes de pago.";
        showToast(msg, "error");
      }
    } finally {
      setRefreshingPayments(false);
    }
  };

  const generateReport = async (rowData) => {
    try {
      const payload = { payment_order_id: rowData.id };
      const response = await downloadFilePost("payment/order/report", payload);

      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : "ordenes_pago.txt";

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast("Ocurrió un error al generar el reporte.", "error");
    }
  };

  const columnsPaymentOrder = [
    { field: "code", headerName: "N° de Orden" },
    {
      field: "date",
      headerName: "Fecha",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return "Fecha inválida";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    { field: "cbu_destinity", headerName: "CBU de destino", flex: 2 },
    {
      field: "amount",
      headerName: "Monto",
      flex: 1,
      align: "right",
      valueFormatter: (params) =>
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(params),
    },
    { field: "status_type", headerName: "Estado", flex: 1 },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleOpenPayment(params.row)}
            disabled={!hasPermission("ver detalle orden de pago")}
          >
            <VisibilityRoundedIcon />
          </IconButton>

          <IconButton
            color="primary"
            onClick={() => generateReport(params.row)}
            disabled={!hasPermission("descargar orden de pago")}
          >
            <DownloadForOfflineRoundedIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const columnsJudicialHistory = [
    { field: "account_number", headerName: "Cuenta", flex: 1 },
    { field: "bank_branch_code", headerName: "Sucursal", flex: 0.7 },
    {
      field: "balance",
      headerName: "Saldo",
      flex: 1,
      align: "right",
      valueFormatter: (params) =>
        params == null || params === ""
          ? "0,00"
          : new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
              minimumFractionDigits: 2,
            }).format(Number(params)),
    },
    {
      field: "balance_at",
      headerName: "Fecha saldo",
      flex: 1,
      renderCell: (params) =>
        params.value ? formatDateTime(params.value) : "—",
    },
    {
      field: "uploaded_at",
      headerName: "Subido",
      flex: 1,
      renderCell: (params) =>
        params.value ? formatDateTime(params.value) : "—",
    },
  ];

  return (
    <>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => handleRefreshPaymentOrders()}
      />

      <Grid container spacing={2}>
        {hasPermission("ver detalle orden de pago") && (
          <Grid size={{ xs: 12, md: 6 }}>
            <GlassCard sx={{ mb: 1.2 }}>
              <SectionTitle
                icon={<DownloadForOfflineRoundedIcon />}
                title="Órdenes de Pago"
                right={
                  <Tooltip
                    title={
                      !online
                        ? ONLINE_QUERIES_OFF_REASON
                        : locked
                          ? LOCKED_LEGAL_FILE_MESSAGE
                          : "Actualizar órdenes de pago"
                    }
                  >
                    <span>
                      <IconButton
                        onClick={handleRefreshPaymentOrders}
                        disabled={
                          refreshingPayments ||
                          locked ||
                          !online ||
                          Boolean(sessionRequiredLawyer) ||
                          !hasPermission("actualizar orden de pago")
                        }
                        sx={{ borderRadius: 2, bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: "rgba(0,0,0,0.07)" } }}
                      >
                        <AutorenewRoundedIcon
                          fontSize="small"
                          sx={{ ...(refreshingPayments && { animation: "spin 1s linear infinite" }) }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              />
            </GlassCard>
            <DatagridCustom
              rows={paymentOrders}
              columns={columnsPaymentOrder}
              title="Listado"
              getRowId={(row) => row.code}
            />
          </Grid>
        )}

        {hasPermission("ver cuentas judiciales detalle") && (
          <Grid size={{ xs: 12, md: 6 }}>
            <GlassCard sx={{ mb: 1.2 }}>
              <SectionTitle
                icon={<MonetizationOnRoundedIcon />}
                title="Saldos de cuentas judiciales"
                description="Saldos actuales e historial de cambios."
              />
            </GlassCard>
            <DatagridCustom
              rows={judicialHistory}
              columns={columnsJudicialHistory}
              title="Histórico"
              getRowId={(row) => row.id}
            />
          </Grid>
        )}
      </Grid>

      <ReceiptPaymentDialog
        open={openPayment}
        onClose={handleClosePayment}
        data={selectedPayment}
      />
    </>
  );
}
